import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Boxes, Award, GraduationCap, Mic2, MessageSquare, ArrowUpRight, Image as ImageIcon, FileText } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getStats } from "@/lib/api";

const STAT_META = [
    { key: "publications", label: "Publications", icon: BookOpen, to: "/admin/publications" },
    { key: "projects", label: "Projects", icon: Boxes, to: "/admin/projects" },
    { key: "awards", label: "Awards", icon: Award, to: "/admin/awards" },
    { key: "courses", label: "Courses", icon: GraduationCap, to: "/admin/courses" },
    { key: "conferences", label: "Conferences", icon: Mic2, to: "/admin/conferences" },
    { key: "unread_messages", label: "Unread messages", icon: MessageSquare, to: "/admin/messages" },
];

const QUICK = [
    { to: "/admin/content/home", label: "Edit Home", icon: FileText },
    { to: "/admin/content/about", label: "Edit About", icon: FileText },
    { to: "/admin/publications", label: "Add Publication", icon: BookOpen },
    { to: "/admin/media", label: "Upload Photo", icon: ImageIcon },
];

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    useEffect(() => { getStats().then(setStats).catch(() => setStats({})); }, []);

    return (
        <div className="space-y-10">
            <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50">Editorial desk</p>
                <h1 className="mt-1 font-display text-4xl sm:text-5xl">Dashboard</h1>
                <p className="mt-2 text-paper/70 max-w-[62ch]">A high-level view of your portfolio’s content. Everything is editable — changes appear on the live site immediately.</p>
            </div>

            <section>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50">Overview</p>
                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {STAT_META.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div key={s.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                                <Link to={s.to} data-testid={`admin-stat-${s.key}`}>
                                    <Card className="group p-5 rounded-2xl bg-white/[0.04] border-white/10 hover:bg-white/[0.06] transition-colors">
                                        <div className="flex items-center justify-between">
                                            <div className="h-9 w-9 rounded-xl bg-white/[0.06] flex items-center justify-center">
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <ArrowUpRight className="h-4 w-4 text-paper/50 group-hover:text-paper transition-colors" />
                                        </div>
                                        <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-paper/60">{s.label}</p>
                                        <p className="mt-1 font-display text-4xl">{stats ? (stats[s.key] ?? 0) : "—"}</p>
                                    </Card>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            <section>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50">Quick actions</p>
                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {QUICK.map((q, i) => {
                        const Icon = q.icon;
                        return (
                            <motion.div key={q.to} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                                <Link to={q.to} data-testid={`admin-quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <Card className="group p-4 rounded-2xl bg-white/[0.03] border-white/10 hover:bg-white/[0.06] transition-colors flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-xl bg-[hsl(var(--sea-glass)/0.2)] flex items-center justify-center">
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <p className="text-sm">{q.label}</p>
                                        <ArrowUpRight className="ml-auto h-4 w-4 text-paper/40 group-hover:text-paper" />
                                    </Card>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            <section>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/50">Live preview</p>
                <div className="mt-3">
                    <Card className="p-5 rounded-2xl bg-white/[0.03] border-white/10">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-display text-2xl">Public site</p>
                                <p className="mt-1 text-sm text-paper/60">Open the live portfolio in a new tab.</p>
                            </div>
                            <Link to="/" target="_blank" className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-xl bg-paper text-ink hover:bg-paper/90" data-testid="admin-open-site-button">
                                Open site <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Card>
                </div>
            </section>
        </div>
    );
}
